import React from 'react'
import { cn } from '@/lib/utils'
import { canonicalizeDomainInput, sanitizeHref } from '@/lib/sanitize'
import ActionCard, { ActionCardType } from './ActionCard'

interface MessageProps {
  role: 'user' | 'assistant'
  content: string
  isSending?: boolean
  isSent?: boolean
}

// Normalize text to handle Unicode variants and hidden characters
function normalizeText(text: string): string {
  return (
    text
      // Remove zero-width characters that can break regex matching
      .replace(/[\u200B\u200C\u200D\uFEFF\u00AD]/g, '')
      // Normalize Unicode asterisk variants to standard asterisk
      .replace(/[＊٭⁎✱]/g, '*')
  )
}

// Process inline markdown (bold, italic, code, links)
function processInlineMarkdown(
  text: string,
  keyPrefix: number
): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Normalize the text first to handle hidden characters
  let remaining = normalizeText(text)
  let partKey = 0

  while (remaining.length > 0) {
    // Match bold (**text**) - simple non-greedy match
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    if (boldMatch && boldMatch.index !== undefined) {
      // Add text before the match
      if (boldMatch.index > 0) {
        parts.push(remaining.slice(0, boldMatch.index))
      }
      // Add the bold text
      parts.push(
        <strong
          key={`bold-${keyPrefix}-${partKey++}`}
          className='font-semibold'
        >
          {boldMatch[1]}
        </strong>
      )
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length)
      continue
    }

    // Match italic (*text*) - single asterisks, avoiding ** patterns
    // Using a simpler regex without lookbehind for browser compatibility
    const italicMatch = remaining.match(/(?:^|[^*])\*([^*]+)\*(?:[^*]|$)/)
    if (italicMatch && italicMatch.index !== undefined) {
      // Adjust index if we matched a character before the *
      const actualIndex = italicMatch[0].startsWith('*')
        ? italicMatch.index
        : italicMatch.index + 1
      const beforeText = remaining.slice(0, italicMatch.index)
      const afterStart =
        italicMatch.index +
        italicMatch[0].length -
        (italicMatch[0].endsWith('*') ? 0 : 1)

      // Add text before the match (including any non-* character we matched)
      if (
        beforeText ||
        (italicMatch.index > 0 && !italicMatch[0].startsWith('*'))
      ) {
        const textBefore =
          italicMatch.index > 0 ? remaining.slice(0, actualIndex) : ''
        if (textBefore) parts.push(textBefore)
      }
      // Add the italic text
      parts.push(
        <em key={`italic-${keyPrefix}-${partKey++}`} className='italic'>
          {italicMatch[1]}
        </em>
      )
      remaining = remaining.slice(afterStart)
      continue
    }

    // Match inline code (`code`)
    const codeMatch = remaining.match(/`(.+?)`/)
    if (codeMatch && codeMatch.index !== undefined) {
      // Add text before the match
      if (codeMatch.index > 0) {
        parts.push(remaining.slice(0, codeMatch.index))
      }
      // Add the code text
      parts.push(
        <code
          key={`code-${keyPrefix}-${partKey++}`}
          className='px-1.5 py-0.5 rounded bg-black/10 dark:bg-white/10 text-xs font-mono'
        >
          {codeMatch[1]}
        </code>
      )
      remaining = remaining.slice(codeMatch.index + codeMatch[0].length)
      continue
    }

    // Match links [text](url)
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/)
    if (linkMatch && linkMatch.index !== undefined) {
      // Add text before the match
      if (linkMatch.index > 0) {
        parts.push(remaining.slice(0, linkMatch.index))
      }
      const safeUrl = sanitizeHref(linkMatch[2])
      if (safeUrl) {
        parts.push(
          <a
            key={`link-${keyPrefix}-${partKey++}`}
            href={safeUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='underline hover:opacity-80 transition-opacity'
          >
            {linkMatch[1]}
          </a>
        )
      } else {
        // Unsafe URL — render the link text as plain text, drop the href
        parts.push(linkMatch[1])
      }
      remaining = remaining.slice(linkMatch.index + linkMatch[0].length)
      continue
    }

    // No markdown found, output the rest as plain text
    parts.push(remaining)
    remaining = ''
  }

  return parts
}

// Lightweight markdown parser for chat messages
// Exported for reuse by the AskPixelmojo answer surface.
export function parseMarkdown(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = []
  let key = 0

  // Normalize text before processing
  const normalizedText = normalizeText(text)

  // Split by lines to handle bullet points and line breaks
  const lines = normalizedText.split('\n')

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      elements.push(<br key={`br-${key++}`} />)
    }

    if (!line.trim()) {
      return // Skip empty lines (already added line break)
    }

    // Check for headings (### Heading)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const headingText = headingMatch[2]

      // Render heading based on level
      if (level === 1) {
        elements.push(
          <h1
            key={`heading-${key++}`}
            className='text-lg md:text-xl font-bold mt-2 mb-1'
          >
            {headingText}
          </h1>
        )
      } else if (level === 2) {
        elements.push(
          <h2
            key={`heading-${key++}`}
            className='text-base md:text-lg font-bold mt-2 mb-1'
          >
            {headingText}
          </h2>
        )
      } else if (level === 3) {
        elements.push(
          <h3
            key={`heading-${key++}`}
            className='text-sm md:text-base font-semibold mt-1.5 mb-0.5'
          >
            {headingText}
          </h3>
        )
      } else if (level === 4) {
        elements.push(
          <h4
            key={`heading-${key++}`}
            className='text-sm font-semibold mt-1 mb-0.5'
          >
            {headingText}
          </h4>
        )
      } else if (level === 5) {
        elements.push(
          <h5
            key={`heading-${key++}`}
            className='text-xs font-semibold mt-1 mb-0.5'
          >
            {headingText}
          </h5>
        )
      } else {
        elements.push(
          <h6
            key={`heading-${key++}`}
            className='text-xs font-medium mt-1 mb-0.5'
          >
            {headingText}
          </h6>
        )
      }
      return
    }

    // Check for bold-style headings (**Heading:**) - treat as section headers
    const boldHeadingMatch = line.match(/^\*\*([^*]+)\*\*:?$/)
    if (boldHeadingMatch) {
      elements.push(
        <h4
          key={`bold-heading-${key++}`}
          className='text-sm font-semibold mt-3 mb-1'
        >
          {boldHeadingMatch[1]}
        </h4>
      )
      return
    }

    // Check for bullet points (• text or - text or * text followed by space)
    const bulletMatch = line.match(/^[•\-]\s+(.+)$/)
    if (bulletMatch) {
      // Process inline markdown within bullet content
      const bulletContent = processInlineMarkdown(bulletMatch[1], key)
      elements.push(
        <div key={`bullet-${key++}`} className='flex gap-2 ml-2'>
          <span className='text-primary mt-0.5'>•</span>
          <span>{bulletContent}</span>
        </div>
      )
      return
    }

    // Check for lines starting with * followed by space (could be bullet)
    // But not ** which is bold
    const asteriskBulletMatch = line.match(/^\*\s+(.+)$/)
    if (asteriskBulletMatch && !line.startsWith('**')) {
      const bulletContent = processInlineMarkdown(asteriskBulletMatch[1], key)
      elements.push(
        <div key={`bullet-${key++}`} className='flex gap-2 ml-2'>
          <span className='text-primary mt-0.5'>•</span>
          <span>{bulletContent}</span>
        </div>
      )
      return
    }

    // Process the line for inline markdown
    const parts = processInlineMarkdown(line, key)
    elements.push(<span key={`line-${key++}`}>{parts}</span>)
  })

  return elements
}

// Card markers the AI embeds in responses, mapped to ActionCard types
const cardMarkers = [
  { marker: '[CONTACT_FORM_CARD]', type: 'contact-form' as ActionCardType },
  { marker: '[CALENDLY_CARD]', type: 'calendly' as ActionCardType },
  { marker: '[PORTFOLIO_CARD]', type: 'portfolio' as ActionCardType },
  { marker: '[PRICING_CARD]', type: 'pricing' as ActionCardType },
]

// The one parameterized marker: [FREE_CHECK_CARD:example.com]. A visitor who
// pastes a bare domain has already named their target, so we carry it through
// to /platform rather than handing them an empty form to retype it into.
const FREE_CHECK_MARKER = /\[FREE_CHECK_CARD(?::([^\]]*))?\]/

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// The domain reaches us via model output echoing user input, so treat it as
// untrusted and run it through the shared canonicalizer. Anything that is not
// a usable hostname degrades to the bare launcher.
function freeCheckUrl(raw: string | undefined): string {
  const domain = canonicalizeDomainInput(raw ?? '')
  if (!domain) return '/platform'
  return `/platform?domain=${encodeURIComponent(domain)}`
}

// Split an assistant response into display text + the action card to show.
// Exported for reuse by the AskPixelmojo answer surface.
export function splitActionCard(content: string): {
  text: string
  cardType: ActionCardType | null
  cardUrl?: string
} {
  let text = content
  let cardType: ActionCardType | null = null
  let cardUrl: string | undefined

  // Free-check wins when several markers appear: a named domain is the
  // strongest intent signal in the conversation.
  const freeCheck = text.match(FREE_CHECK_MARKER)
  if (freeCheck) {
    cardType = 'free-check'
    cardUrl = freeCheckUrl(freeCheck[1])
    text = text.replace(new RegExp(FREE_CHECK_MARKER.source, 'gi'), '').trim()
  }

  const cardToShow = cardMarkers.find(({ marker }) => content.includes(marker))
  if (!cardType && cardToShow) cardType = cardToShow.type
  cardMarkers.forEach(({ marker }) => {
    text = text.replace(new RegExp(escapeRegExp(marker), 'g'), '').trim()
  })

  return { text, cardType, cardUrl }
}

export default function Message({ role, content }: MessageProps) {
  const isUser = role === 'user'

  const { text: displayContent, cardType, cardUrl } = splitActionCard(content)

  return (
    <div
      className={cn(
        'flex items-end gap-2',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      {!isUser && (
        <div className='w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center flex-shrink-0 mb-1'>
          <span className='text-xs font-semibold text-muted-foreground'>
            PM
          </span>
        </div>
      )}
      <div className='flex flex-col gap-3 max-w-[85%] md:max-w-[75%]'>
        {/* Message text */}
        {displayContent && (
          <div
            className={cn(
              'px-4 py-2.5 md:px-5 md:py-3',
              isUser
                ? 'bg-primary text-white shadow-[0_2px_8px_rgba(0,141,204,0.2)] rounded-[18px_18px_4px_18px]'
                : 'bg-card text-foreground border border-border shadow-[0_1px_3px_rgba(0,0,0,0.1)] rounded-[18px_18px_18px_4px]'
            )}
          >
            <div className='text-sm md:text-base whitespace-pre-wrap leading-relaxed'>
              {parseMarkdown(displayContent)}
            </div>
          </div>
        )}

        {/* Action Card */}
        {!isUser && cardType && (
          <ActionCard type={cardType} url={cardUrl} className='max-w-full' />
        )}
      </div>
      {isUser && (
        <div className='w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mb-1'>
          <span className='text-xs font-semibold text-white'>U</span>
        </div>
      )}
    </div>
  )
}
